import { ref, watch, onUnmounted } from 'vue'
import { audioUnlocked } from '@/utilities/audioUnlock'

/**
 * Composable to manage inbound call notifications
 * Handles looping audio and document title updates
 */
export function useInboundCallNotifications() {
  const audioElement = ref<HTMLAudioElement | null>(null)
  const originalTitle = ref<string>('Retain') // Default title, will be updated when first accessed

  /**
   * Starts playing the inbound call ringtone on loop
   */
  function startRinging() {
    if (!audioUnlocked.value) {
      return
    }

    // If audio is already playing, don't restart
    if (audioElement.value && !audioElement.value.paused) {
      return
    }

    try {
      // Create new audio element if it doesn't exist
      if (!audioElement.value) {
        audioElement.value = new Audio('https://cdn.retainauto.com/audio/calls-in-queue-2.mp3')
        audioElement.value.loop = true
        audioElement.value.addEventListener('error', e => {
          console.error('Audio loading failed for calls-in-queue-2.mp3:', e)
        })
      }

      // Play the audio
      audioElement.value.play().catch(error => {
        console.error('Audio playback failed for calls-in-queue-2.mp3:', error)
      })
    } catch (error) {
      console.error('Failed to create or play audio for calls-in-queue-2.mp3:', error)
    }
  }

  /**
   * Stops playing the inbound call ringtone
   */
  function stopRinging() {
    if (audioElement.value) {
      audioElement.value.pause()
      audioElement.value.currentTime = 0
    }
  }

  /**
   * Updates the document title to show phone emoji when there are queued calls
   * @param hasQueuedCalls - Whether there are calls in the queue
   */
  function updateDocumentTitle(hasQueuedCalls: boolean) {
    // Store the original title (without emoji) if we haven't stored it yet
    if (!document.title.includes('📞') && document.title !== originalTitle.value) {
      originalTitle.value = document.title || 'Retain'
    }

    if (hasQueuedCalls) {
      document.title = '📞 Retain'
    } else {
      document.title = originalTitle.value
    }
  }

  /**
   * Manages notifications based on queued calls count
   * @param queuedCallsCount - Number of calls in queue
   */
  function manageNotifications(queuedCallsCount: number) {
    const hasQueuedCalls = queuedCallsCount > 0

    if (hasQueuedCalls) {
      startRinging()
      updateDocumentTitle(true)
    } else {
      stopRinging()
      updateDocumentTitle(false)
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopRinging()
    updateDocumentTitle(false)
    if (audioElement.value) {
      audioElement.value = null
    }
  })

  return {
    startRinging,
    stopRinging,
    updateDocumentTitle,
    manageNotifications
  }
}

